import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, UtensilsCrossed } from "lucide-react";
import { api, formatApiError, fmtUSD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import Modal from "@/components/Modal";
import { Label, Input, Textarea, PrimaryButton, GhostButton } from "@/components/fields";

const EMPTY = {
  item_id: "", name: "", email: "", whatsapp: "", reservation_ref: "",
  start_date: "", end_date: "", meal_times: "", adults: 2, children: 0, allergies: "",
};

export default function FoodRequestModal({ open, onClose, prefill }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [menu, setMenu] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setDone(false);
      setForm({
        ...EMPTY,
        ...(prefill || {}),
        name: prefill?.name || user?.name || "",
        email: prefill?.email || user?.email || "",
      });
      api.get("/food-services/menu").then((r) => setMenu(r.data)).catch(() => {});
    }
  }, [open, prefill, user]);

  const item = useMemo(() => menu?.items?.find((i) => i.id === form.item_id), [menu, form.item_id]);
  const guests = Number(form.adults || 0) + Number(form.children || 0);
  const billed = Math.max(guests, menu?.min_guests || 4);
  const days = useMemo(() => {
    if (!item?.per_day || !form.start_date) return 1;
    const d0 = new Date(form.start_date);
    const d1 = form.end_date ? new Date(form.end_date) : d0;
    return Math.max(1, Math.round((d1 - d0) / 86400000) + 1);
  }, [item, form.start_date, form.end_date]);
  const estimate = item && item.price ? item.price * billed * days : null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.item_id) return toast.error(t("food.service"));
    if (!form.start_date) return toast.error(t("food.dateSingle"));
    if (form.name.trim().length < 2) return toast.error(t("food.name"));
    if (!form.email.includes("@")) return toast.error(t("food.email"));
    setSending(true);
    try {
      await api.post("/food-services/request", { ...form, lang });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} wide>
      {done ? (
        <div className="text-center py-6 space-y-4" data-testid="food-success">
          <span className="mx-auto w-14 h-14 rounded-full bg-teal/15 text-teal flex items-center justify-center">
            <Check size={26} />
          </span>
          <h3 className="font-serif text-2xl text-ink">{t("food.done")}</h3>
          <p className="text-sm text-ink/60 leading-relaxed max-w-sm mx-auto">{t("food.received")}</p>
          <PrimaryButton data-testid="food-done-btn" onClick={onClose} className="w-full">OK</PrimaryButton>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-2xl bg-terracotta/10 text-terracotta flex items-center justify-center">
              <UtensilsCrossed size={19} />
            </span>
            <h3 className="font-serif text-xl text-ink">{t("food.modalTitle")}</h3>
          </div>

          <div>
            <Label htmlFor="fr-service">{t("food.service")}</Label>
            <select id="fr-service" data-testid="food-service-select" value={form.item_id} onChange={set("item_id")}
                    className="w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-terracotta">
              <option value="">—</option>
              {(menu?.items || []).filter((i) => i.active).map((i) => (
                <option key={i.id} value={i.id} data-testid={`food-option-${i.id}`}>
                  {lang === "es" ? i.label_es : i.label_en} — {i.price ? `$${i.price} ${t(i.per_day ? "food.perPersonDay" : "food.perPerson")}` : t("food.quote")}
                </option>
              ))}
            </select>
          </div>

          <div className={`grid gap-3 ${item?.per_day ? "grid-cols-2" : "grid-cols-1"}`}>
            <div>
              <Label htmlFor="fr-start">{item?.per_day ? t("food.dateFrom") : t("food.dateSingle")}</Label>
              <Input id="fr-start" data-testid="food-start-input" type="date" value={form.start_date} onChange={set("start_date")} />
            </div>
            {item?.per_day && (
              <div>
                <Label htmlFor="fr-end">{t("food.dateTo")}</Label>
                <Input id="fr-end" data-testid="food-end-input" type="date" value={form.end_date} onChange={set("end_date")} />
              </div>
            )}
          </div>
          <p className="text-[11px] text-ink/45 -mt-2">{t("food.advanceNote")}</p>

          <div>
            <Label htmlFor="fr-times">{t("food.times")}</Label>
            <Input id="fr-times" data-testid="food-times-input" value={form.meal_times} onChange={set("meal_times")} placeholder="8:00 / 13:00 / 19:00" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="fr-adults">{t("food.adults")}</Label>
              <Input id="fr-adults" data-testid="food-adults-input" type="number" min="1" max="20" value={form.adults} onChange={set("adults")} />
            </div>
            <div>
              <Label htmlFor="fr-children">{t("food.children")}</Label>
              <Input id="fr-children" data-testid="food-children-input" type="number" min="0" max="20" value={form.children} onChange={set("children")} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="fr-name">{t("food.name")}</Label>
              <Input id="fr-name" data-testid="food-name-input" value={form.name} onChange={set("name")} />
            </div>
            <div>
              <Label htmlFor="fr-email">{t("food.email")}</Label>
              <Input id="fr-email" data-testid="food-email-input" type="email" value={form.email} onChange={set("email")} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="fr-wa">{t("food.whatsapp")}</Label>
              <Input id="fr-wa" data-testid="food-whatsapp-input" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+1 809 ..." />
            </div>
            <div>
              <Label htmlFor="fr-ref">{t("food.ref")}</Label>
              <Input id="fr-ref" data-testid="food-ref-input" value={form.reservation_ref} onChange={set("reservation_ref")} />
            </div>
          </div>

          <div>
            <Label htmlFor="fr-allergies">{t("food.allergies")}</Label>
            <Textarea id="fr-allergies" data-testid="food-allergies-input" rows={3} value={form.allergies} onChange={set("allergies")} />
          </div>

          {item && (
            <div className="rounded-2xl bg-night text-sand p-4 flex items-center justify-between" data-testid="food-estimate">
              <span className="text-xs text-sand/60 font-mono uppercase tracking-[0.12em]">{t("food.estimate")}</span>
              {estimate != null ? (
                <span className="text-right">
                  <span className="font-serif text-2xl text-gold" data-testid="food-estimate-total">${fmtUSD(estimate)}</span>
                  <span className="block text-[11px] text-sand/50">
                    ${fmtUSD(item.price)} × {billed} {t("food.guestsSuffix") || "guests"}{item.per_day ? ` × ${days}d` : ""}
                  </span>
                </span>
              ) : (
                <span className="text-sm text-gold font-semibold" data-testid="food-quote-req">{t("food.quoteReq")}</span>
              )}
            </div>
          )}
          {estimate != null && billed > guests && (
            <p className="text-[11px] text-ink/50" data-testid="food-min-note">{t("food.minNote", { billed })}</p>
          )}

          <div className="flex flex-col sm:flex-row gap-2 justify-end pt-1">
            <GhostButton data-testid="food-cancel-btn" onClick={onClose}>{t("food.back")}</GhostButton>
            <PrimaryButton data-testid="food-submit-btn" onClick={submit} disabled={sending}>
              {sending ? t("food.sending") : t("food.submit")} <Check size={15} />
            </PrimaryButton>
          </div>
        </div>
      )}
    </Modal>
  );
}
